import dayjs, { Dayjs } from "dayjs";
import { MonthlySpendEntry } from "./types";

// UI-side selection state. The api's canonical form is {fromMonth,toMonth};
// the UI keeps the mode so the Segmented toggle and pickers stay stable.
export type Period =
  | { mode: "year"; year: number }
  | { mode: "range"; fromMonth: string; toMonth: string };

export function periodQueryParams(
  period: Period
): { year: number } | { fromMonth: string; toMonth: string } {
  return period.mode === "year"
    ? { year: period.year }
    : { fromMonth: period.fromMonth, toMonth: period.toMonth };
}

// A range that picks Jan..Dec of one year is shape-equal to year mode
// (api parity, spec 10.5): print it the same way.
const rangeIsFullYear = (fromMonth: string, toMonth: string): boolean =>
  fromMonth.endsWith("-01") &&
  toMonth.endsWith("-12") &&
  fromMonth.slice(0, 4) === toMonth.slice(0, 4);

export function formatPeriodLabel(period: Period): string {
  if (period.mode === "year") return String(period.year);
  if (rangeIsFullYear(period.fromMonth, period.toMonth)) {
    return period.fromMonth.slice(0, 4);
  }
  const from = dayjs(`${period.fromMonth}-01`).format("MMM YYYY");
  const to = dayjs(`${period.toMonth}-01`).format("MMM YYYY");
  return `${from} - ${to}`;
}

export function periodFileToken(period: Period): string {
  if (period.mode === "year") return String(period.year);
  if (rangeIsFullYear(period.fromMonth, period.toMonth)) {
    return period.fromMonth.slice(0, 4);
  }
  return `${period.fromMonth}_${period.toMonth}`;
}

export function isSamePeriod(a: Period, b: Period): boolean {
  if (a.mode !== b.mode) return false;
  if (a.mode === "year" && b.mode === "year") return a.year === b.year;
  if (a.mode === "range" && b.mode === "range") {
    return a.fromMonth === b.fromMonth && a.toMonth === b.toMonth;
  }
  return false;
}

// Inclusive first and last day of the period, for endpoints that take
// day-level from/to (invoice-lines). processed_invoice.invoice_date is a
// DATE column, so both edges match exactly when sent as YYYY-MM-DD.
export function periodDayBounds(period: Period): [Dayjs, Dayjs] {
  const fromMonth =
    period.mode === "year" ? `${period.year}-01` : period.fromMonth;
  const toMonth = period.mode === "year" ? `${period.year}-12` : period.toMonth;
  return [
    dayjs(`${fromMonth}-01`).startOf("month"),
    dayjs(`${toMonth}-01`).endOf("month"),
  ];
}

// The drawer's invoice-lines picker may narrow inside the page selection but
// never step outside it, so both edge days stay selectable.
export function isOutsidePeriod(period: Period, date: Dayjs): boolean {
  const [from, to] = periodDayBounds(period);
  return date.isBefore(from, "day") || date.isAfter(to, "day");
}

export function monthlySpendChartData(
  entries: MonthlySpendEntry[]
): { month: string; spend: number }[] {
  const crossYear =
    entries.length > 0 &&
    entries[0].month.slice(0, 4) !== entries[entries.length - 1].month.slice(0, 4);
  return entries.map((entry) => ({
    month: dayjs(`${entry.month}-01`).format(crossYear ? "MMM YY" : "MMM"),
    spend: entry.spend,
  }));
}
